import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, DotDotDotVerticalIcon, StreamrNewLogoSolo, } from '@streamr/tv-ui';
import { useRef } from 'react';
import styled from 'styled-components';
import { BroadcastService } from '../../services/broadcast/BroadcastService';
import { MediaService } from '../../services/media/MediaService';
import { SceneService } from '../../services/scene/SceneService';
import { useBroadcastUrl } from '../../utils/url';
import { Anchor } from '../Anchor';
import { RecordingStatusIndicator } from '../broadcaster/helpers/RecordingStatusIndicator';
import { ToggleLiveButton } from '../broadcaster/helpers/ToggleLiveButton';
import { AudioDeviceButton, VideoDeviceButton } from '../DeviceButton';
import { LiveIndicatorWithCount } from '../LiveIndicator';
import { showBroadcastPopup } from '../popups/BroadcastPopup';
import { selectCamera } from '../popups/devices/CameraSelectorPopup';
import { selectMicrophone } from '../popups/devices/MicrophoneSelectorPopup';
import { ShareButtonRound } from '../ShareButtons';
import { BottomLeftSlot, BottomRightSlot, HudSlots, TopLeftSlot, TopRightSlot, } from '../shared/HudSlots';
import { LogoLink } from '../shared/LogoLink';
import { MobilePluginButtonTray } from '../shared/PluginButtonTray';
export function BroadcasterViewMobileHud({ activeBroadcast, fullscreenButton, }) {
    const settingsButtonAnchorRef = useRef(null);
    const broadcastUrl = useBroadcastUrl(activeBroadcast);
    const displayScene = SceneService.getOutput().useDisplayScene();
    const broadcastService = BroadcastService.getNotificationInterface();
    const broadcastOutputBroadcasting = BroadcastService.getOutput().useBroadcasting();
    const broadcastOutputServiceRunning = BroadcastService.getOutput().useServiceRunning();
    const mediaService = MediaService.getNotificationInterface();
    const mediaOutputAudioMuted = MediaService.getOutput().useAudioMuted();
    const mediaOutputVideoMuted = MediaService.getOutput().useVideoMuted();
    const connectedVideoDevices = MediaService.getOutput().useConnectedVideoDevices();
    const connectedAudioDevices = MediaService.getOutput().useConnectedAudioDevices();
    const onMuteAudioButtonClicked = () => {
        if (mediaOutputAudioMuted) {
            mediaService.notifyAudioMutingChangeRequested(false);
        }
        else {
            mediaService.notifyAudioMutingChangeRequested(true);
        }
    };
    const onMuteVideoButtonClicked = () => {
        if (mediaOutputVideoMuted) {
            mediaService.notifyVideoMutingChangeRequested(false);
        }
        else {
            mediaService.notifyVideoMutingChangeRequested(true);
        }
    };
    return (_jsxs(Hud, { "$visible": true, children: [_jsx(TopLeftSlot, { children: _jsx(LogoLink, { logoImage: _jsx(StreamrNewLogoSolo, {}) }) }), _jsx(TopRightSlot, { children: broadcastOutputBroadcasting && _jsx(LiveIndicatorWithCount, {}) }), _jsxs(StyledBottomLeftSlot, { children: [_jsx(AudioDeviceButton, { devicePresent: connectedAudioDevices.length > 0, muted: mediaOutputAudioMuted, onMuteClick: onMuteAudioButtonClicked, onSelectClick: (anchor) => {
                            selectMicrophone({
                                anchorRef: anchor,
                                variant: 'top-center',
                            });
                        } }), _jsx(VideoDeviceButton, { devicePresent: connectedVideoDevices.length > 0, muted: mediaOutputVideoMuted, onMuteClick: onMuteVideoButtonClicked, onSelectClick: (anchor) => {
                            selectCamera({
                                anchorRef: anchor,
                                variant: 'top-center',
                            });
                        } }), _jsx(Anchor, { ref: settingsButtonAnchorRef, children: _jsx(SettignsButton, { type: "button", onClick: () => {
                                showBroadcastPopup({
                                    broadcast: activeBroadcast,
                                    anchorRef: settingsButtonAnchorRef,
                                    variant: 'mobile',
                                    context: 'broadcaster',
                                    onVideoQualityChanged: (quality) => {
                                        broadcastService.notifyVideoQualityChanged(quality);
                                    },
                                });
                            }, children: _jsx(DotDotDotVerticalIcon, {}) }) }), _jsx(ToggleLiveButton, { device: "mobile", broadcastServiceIsRunning: broadcastOutputServiceRunning, isBroadcasting: broadcastOutputBroadcasting, onGoLiveClick: () => {
                            if (displayScene) {
                                SceneService.getNotificationInterface().notifySetLiveScene(displayScene.id);
                            }
                            broadcastService.notifyStartBroadcast();
                        }, onEndBroadcastClick: () => {
                            broadcastService.notifyStopBroadcast();
                        } })] }), _jsxs(StyledBottomRightSlot, { children: [_jsx(RecordingStatusIndicator, { isMobile: true, broadcast: activeBroadcast }), fullscreenButton, _jsx(ShareButtonRound, { url: broadcastUrl }), _jsx(MobilePluginButtonTray, { slotId: "buttonstray" })] }), _jsx("div", {})] }));
}
const SettignsButton = styled(Button) `
    --Button_Background: var(--tv-ui-color-12);
    --Button_HoverBackground: var(--tv-ui-color-12);
    --Button_TextColor: var(--tv-ui-color-3);
    --Button_BorderRadius: 1.5rem;
    --Button_Height: 3rem;
    --Button_Padding: 0 1rem;
    backdrop-filter: blur(10px);
`;
const Hud = styled(HudSlots) `
    --tv-HudSlots-offset: 1rem;
    z-index: 10;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.3s ease-in-out;
    pointer-events: none;
`;
const StyledBottomLeftSlot = styled(BottomLeftSlot) `
    box-sizing: border-box;
    --tv-HudSlots-offset: 0;
    --tv-HudBottomSlot-offset: 1rem;
    width: 100%;
    display: flex;
    gap: 0.5rem;
    padding: 0 1rem;
    align-items: center;
    justify-content: center;

    @media (min-width: 25rem) {
        gap: 0.75rem;
    }

    @media(orientation: landscape) {
        width: auto;
        left: 1rem;
        padding: 0
    }
`;
const StyledBottomRightSlot = styled(BottomRightSlot) `
    --tv-HudSlots-offset: 0;
    bottom: 50%;
    right: 1rem;
    transform: translateY(50%);
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-end;
    justify-content: center;

    @media(orientation: landscape) {
        bottom: 1rem;
        right: 1rem;
        transform: none;
    }
`;
